<template>
  <div class="select-field" :class="{ 'select-field--block': block }">
    <button
      type="button"
      class="select-field-input"
      :class="{ open, 'is-placeholder': !selected }"
      :disabled="disabled"
      @click.stop="toggle"
    >
      <span class="select-field-value">{{ selected ? selected.label : (placeholder || '—') }}</span>
    </button>

    <!-- In the body, so a card, a table or a dialogue cannot clip it. -->
    <Teleport to="body">
      <div v-if="open" class="select-menu" :style="menuStyle">
        <button
          v-for="option in options"
          :key="String(option.value)"
          type="button"
          class="select-menu-item"
          :class="{ selected: isSame(option.value, modelValue) }"
          @click="choose(option)"
        >
          {{ option.label }}
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
/**
 * A select with the app's own list under it.
 *
 * `<select>` is drawn by the browser, not by us: the list it opens is the
 * operating system's, in its own font, its own blue and its own corners, and
 * no stylesheet reaches it. Next to the dashboard's menus it reads as part of
 * another program.
 *
 * So the field is a button showing the chosen label, and the list is one more
 * of the app's dropdowns — white panel, hairline border, soft shadow, blue
 * tint on the chosen row — put in the body so nothing clips it and placed
 * under the button that opened it.
 *
 * The value stays whatever was bound to the `<select>`, so it drops into
 * anything that used one.
 */
import { computed, onMounted, onUnmounted, ref } from 'vue';

const props = defineProps({
    modelValue: { type: [String, Number, Boolean, null], default: null },
    /** [{ value, label }] — the options the `<select>` used to list. */
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    /** Fills the width it is given, as a `<select>` in a form does. */
    block: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue', 'change']);

const open = ref(false);
const menuStyle = ref({});

/* Loose comparison on purpose: a `<select>` hands back strings, and the model
   behind it is often a number. */
const isSame = (a, b) => String(a) === String(b);

const selected = computed(
    () => props.options.find((option) => isSame(option.value, props.modelValue)) || null,
);

const place = (button) => {
    const box = button.getBoundingClientRect();
    const below = window.innerHeight - box.bottom;
    const height = Math.min(260, props.options.length * 28 + 8);

    menuStyle.value = {
        // Upwards when the field is near the foot of the screen.
        top: below < height + 12
            ? `${Math.max(8, box.top - height - 4)}px`
            : `${box.bottom + 4}px`,
        left: `${Math.max(8, Math.min(box.left, window.innerWidth - box.width - 8))}px`,
        minWidth: `${Math.max(box.width, 120)}px`,
        maxWidth: `${Math.max(box.width, 320)}px`,
    };
};

const toggle = (event) => {
    if (props.disabled) return;

    if (open.value) {
        open.value = false;

        return;
    }

    place(event.currentTarget);
    open.value = true;
};

const close = () => {
    open.value = false;
};

const choose = (option) => {
    close();
    emit('update:modelValue', option.value);
    emit('change', option.value);
};

const onOutside = (event) => {
    if (!open.value) return;
    if (event.target.closest('.select-menu') || event.target.closest('.select-field-input')) return;
    close();
};

const onKey = (event) => {
    if (event.key === 'Escape') close();
};

/* The menu is placed in viewport coordinates, so whatever moves under it has
   to close it. Heard in the capture phase, which is the only way to hear a
   scrolling container — but that includes the menu's own list, and closing on
   that would make a long list impossible to scroll. */
const onScroll = (event) => {
    if (event?.target?.closest?.('.select-menu')) return;
    close();
};

onMounted(() => {
    document.addEventListener('pointerdown', onOutside, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
});

onUnmounted(() => {
    document.removeEventListener('pointerdown', onOutside, true);
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', close);
});
</script>

<style scoped>
.select-field {
    display: inline-flex;
    min-width: 0;
}

/* A class the call site kept — form-input, form-control, filter-input — lands
   on this root, because that is where Vue puts a fall-through class, and it
   brings a native field's padding, border and background with it. The frame
   is the button's job, so the root carries none of it. !important because the
   page's rule and this one are of equal weight and the page's comes later. */
.select-field {
    padding: 0 !important;
    border: none !important;
    background: none !important;
    box-shadow: none !important;
    height: auto !important;
    min-height: 0 !important;
    line-height: normal !important;
}

.select-field--block {
    display: flex;
    width: 100%;
}

/* The field: the same quiet control as the pickers beside it, with the
   chevron drawn on it rather than supplied by the browser. */
.select-field .select-field-input {
    display: inline-flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    padding: 7px 26px 7px 10px;
    background: #fff;
    border: 1px solid var(--border, #dde3ea);
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: var(--text-primary, #1e293b);
    text-align: left;
    cursor: pointer;
    transition: all 0.15s ease;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12' fill='none' stroke='%23374151' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m2.5 4.5 3.5 3.5 3.5-3.5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 9px center;
}

.select-field .select-field-input:hover:not(:disabled) {
    border-color: var(--primary, #0697e4);
}

.select-field .select-field-input.open {
    border-color: var(--primary, #0697e4);
    color: var(--primary, #0697e4);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12' fill='none' stroke='%232b82f0' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m2.5 7.5 3.5-3.5 3.5 3.5'/%3E%3C/svg%3E");
}

.select-field .select-field-input:disabled {
    color: var(--text-light, #64748b);
    background-color: #f8fafc;
    cursor: default;
}

.select-field .select-field-input:focus-visible {
    outline: 2px solid var(--primary, #0697e4);
    outline-offset: 1px;
}

.select-field .select-field-value {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.select-field .select-field-input.is-placeholder .select-field-value {
    color: var(--text-light, #64748b);
}

/* The list, dressed like every other menu in the app. */
.select-menu {
    position: fixed;
    /* Over every layer the app stacks: modal overlays at 9999, a modal opened
       from a modal at 10100, the original-invoice preview at 10200. A menu
       below one of those still opens — it is simply painted underneath, and
       reads as a dropdown that does nothing. */
    z-index: 100100;
    max-height: 260px;
    padding: 4px;
    background: #fff;
    border: 1px solid var(--border, #dde3ea);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1);
    overflow-y: auto;
    /* At the end of the list the wheel would otherwise carry on to the page
       behind, which moves the field and closes the menu. */
    overscroll-behavior: contain;
    font-family: inherit;
}

.select-menu-item {
    display: block;
    width: 100%;
    padding: 5px 10px;
    border: none;
    border-radius: 6px;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-primary, #1e293b);
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    transition: background 0.15s ease;
}

.select-menu-item:hover {
    background: var(--primary-light, #e0f2fe);
}

.select-menu-item.selected {
    color: var(--primary, #0697e4);
    font-weight: 700;
}
</style>
